import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster } from "../../components/ui/Toaster";
import CollapsibleSidebar from "../../components/menu/CollapsibleSidebar";
import MobileDrawer from "../../components/menu/MobileDrawer";
import NewAdminMenu from "../../components/menu/newAdminMenu";
import ThemeToggle from "../../components/themeToggle";
import LanguageSwitcher from "../../components/languageSwitcher/LanguageSwitcher";
import NotificationsBell from "../../components/menu/NotificationsBell";
import UserMenu from "../../components/menu/UserMenu";
import BottomNavigation from "../../components/bottomNav/BottomNavigation";
import AIChatWidget from "../../components/aiChat/AIChatWidget";
import AnnouncementsHost from "../../components/announcements/AnnouncementsHost";
import useAuth from "../../hooks/useAuth";
import { SidebarProvider, useSidebar } from "../../providers/SidebarProvider";
import { rememberConnectReturn } from "../../utils/connectReturn";
import "./mainLayout.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Inner layout — accede al contexto del sidebar
// ─────────────────────────────────────────────────────────────────────────────
function MainLayoutInner({ routes }) {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();
  const [updatePackages, setUpdatePackages] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed } = useSidebar();
  const mainRef = useRef(null);

  useEffect(() => {
    if (!user && !isLoading) {
      // Si la tienda trajo al comercio a autorizar una conexión, el login lo
      // devuelve ahí en vez de a /home (solo aplica a esa pantalla).
      rememberConnectReturn();
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  // Al cambiar de ruta, volver el scroll al tope.
  // El contenedor scrolleable es .layout-main (no window), por eso usamos el ref.
  // También reseteamos window por si alguna vista scrollea el documento.
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div
      className="main-layout-root"
      style={{ backgroundColor: "var(--bg-primary)" }}
    >
      <Toaster />

      <div className="main-layout-body">
        {/* ── Sidebar (oculto en mobile, visible en desktop) ────────── */}
        <div
          className={`sidebar-column${isCollapsed ? " sidebar-column--collapsed" : " sidebar-column--expanded"}`}
          aria-hidden="false"
        >
          <CollapsibleSidebar fetch={setUpdatePackages} />
        </div>

        {/* ── Área de contenido ─────────────────────────────────────── */}
        <div className="content-column">
          {/* Header */}
          <header className="layout-header">
            {/* Hamburger — solo visible en mobile (display vía SCSS @media). */}
            <button
              type="button"
              className="layout-header__hamburger"
              onClick={() => setDrawerOpen(true)}
              aria-label={t("Abrir menú")}
              aria-expanded={drawerOpen}
              aria-controls="mobile-nav-drawer"
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                menu
              </span>
            </button>

            <NewAdminMenu fetch={setUpdatePackages}>
              <LanguageSwitcher />
              <ThemeToggle />
              <NotificationsBell />
              <UserMenu />
            </NewAdminMenu>
          </header>

          {/* Contenido principal */}
          <main className="layout-main" ref={mainRef}>
            <Routes>
              {routes.map((route, index) => (
                <Route
                  key={index}
                  path={route.path}
                  element={
                    <div className="content-main-container">
                      <route.element
                        update={updatePackages}
                        resetUpdate={setUpdatePackages}
                      />
                    </div>
                  }
                />
              ))}
            </Routes>
          </main>
        </div>
      </div>

      {/* ── Widget de chat IA (solo rol cliente) ─────────────────────── */}
      <AIChatWidget />

      {/* ── Anuncios post-login (registry en components/announcements) ────
          El host se auto-gatea: solo rol comercio, muestra el primer anuncio
          elegible no descartado ("no volver a mostrar" = localStorage por
          aviso). Para agregar anuncios nuevos, ver registry.js. */}
      <AnnouncementsHost />

      {/* ── Bottom navigation (solo mobile) ─────────────────────────── */}
      <BottomNavigation onOpenDrawer={() => setDrawerOpen(true)} />

      {/* ── Mobile drawer (sidebar deslizable en <992px) ──────────────── */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        fetch={setUpdatePackages}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Exportación principal con SidebarProvider
// ─────────────────────────────────────────────────────────────────────────────
export default function MainLayout({ routes }) {
  return (
    <SidebarProvider>
      <MainLayoutInner routes={routes} />
    </SidebarProvider>
  );
}
